import { Link } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import { getOauthProviderIcon } from '@client/util/getOauthProviderIcon';
import { useSignupForm } from '@client/hooks/useSignupForm';
import { Spinner } from '@design-system';
import { AlertCircle, Briefcase, ChevronRight, ClipboardList, Loader2, Mail, Phone } from 'lucide-react';

const INTAKE_FORM_URL = 'https://app.waterlily.com/onboard/yta4-yh4?hasAccessToResults=true';

export function Signup() {
  const {
    form,
    onSubmit,
    handleSocialLogin,
    handlePhoneNumberChange,
    isChecking,
    isRedirecting,
    existingAdvisor,
    clearExistingAdvisorError,
    isPasswordMode,
  } = useSignupForm();
  const isSubmitting = form.formState.isSubmitting || isChecking;
  const isFormInvalid = !form.formState.isValid;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {isRedirecting && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-white/70">
          <Spinner className="size-8 text-primary" />
        </div>
      )}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
        <Link
          to="/"
          className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
        >
          Already have an account?{' '}
          <span className="text-primary hidden font-medium sm:inline">Sign in</span>
          <span className="text-primary font-medium sm:hidden">Log in</span>
        </Link>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-18 md:pt-20">
        <div className="w-full max-w-md space-y-5">
          {/* Advisor Signup Card */}
          <Card className="overflow-hidden border bg-background p-0 gap-0 rounded-2xl shadow-none">
            <div className="p-1">
              <div className="rounded-xl bg-muted px-4 py-8 text-center">
                <div className="mb-2 flex items-center justify-center gap-2">
                  <Briefcase className="size-5 text-primary-dark" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary-dark">
                    Advisor Sign-Up
                  </span>
                </div>
                <h1 className="text-xl font-bold text-gray-950">
                  {isPasswordMode ? 'Dev: Password Signup' : 'Create your advisor account'}
                </h1>
                <p className="mx-auto mt-1 max-w-xs text-sm text-foreground">
                  {isPasswordMode
                    ? 'Create an account with email and password (local dev only)'
                    : "We'll verify your email and phone to protect you and your clients"}
                </p>
              </div>
            </div>
            <CardContent className="p-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Mail className="h-4 w-4 text-gray-400" />
                          Email address
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="you@example.com"
                            className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                            autoFocus
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!isPasswordMode && (
                    <FormField
                      control={form.control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                            <Phone className="h-4 w-4 text-gray-400" />
                            Phone number
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="(555) 555-5555"
                              className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                              {...field}
                              onChange={handlePhoneNumberChange}
                            />
                          </FormControl>
                          <FormDescription className="text-xs text-gray-600">
                            Verification texts only. No spam.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {existingAdvisor && (
                    <Alert variant="warning" className="border-amber-200 bg-amber-50">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        {existingAdvisor.matchedBy === 'email' && (
                          <>An account with this email already exists.</>
                        )}
                        {existingAdvisor.matchedBy === 'phone' && (
                          <>An account with this phone number already exists.</>
                        )}
                        {existingAdvisor.matchedBy === 'both' && (
                          <>An account with this email and phone number already exists.</>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {existingAdvisor ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-lg border-gray-200 py-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
                      asChild
                    >
                      <Link to="/" onClick={clearExistingAdvisorError}>
                        Sign in to your account
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      className="w-full py-6"
                      disabled={isSubmitting || isFormInvalid}
                    >
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {isPasswordMode ? 'Continue with password' : 'Send verification code'}
                    </Button>
                  )}

                  <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-gray-200">
                    <span className="relative z-10 bg-white px-4 text-gray-500">
                      Or continue with
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center gap-2 rounded-lg border-gray-200 py-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
                      onClick={() => handleSocialLogin('google-oauth2')}
                    >
                      {getOauthProviderIcon('google')}
                      Google
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-center gap-2 rounded-lg border-gray-200 py-6 font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50"
                      onClick={() => handleSocialLogin('windowslive')}
                    >
                      {getOauthProviderIcon('microsoft')}
                      Outlook
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Consumer Intake Form Option */}
          <a
            href={INTAKE_FORM_URL}
            className="group block"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Card className="border border-primary/20 bg-primary/5 p-0 rounded-2xl shadow-none transition-all hover:border-primary/50">
              <CardContent className="flex items-center gap-4 p-4 md:p-6">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/20 transition-all group-hover:bg-primary">
                  <ClipboardList className="size-6 text-primary transition-all group-hover:text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-primary-dark transition-all group-hover:text-primary">
                    Want to plan for your own care?
                  </p>
                  <p className="text-sm text-primary-dark/80 transition-all group-hover:text-primary">
                    Fill out an intake form to get your personalized results
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary" />
              </CardContent>
            </Card>
          </a>
        </div>
      </main>
    </div>
  );
}
